// Figma "Карта заявок": an order opened into its jobs, newest attempt on top.
import { useMemo } from "react";
import type { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { DataTable, type ColumnConfigState } from "@racoon/ui";
import { useListJobsQuery } from "~/shared/api/jobs/job.grpc";
import { useListDriverIncidentsQuery } from "~/shared/api/driver-incidents/driver-incidents.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { adaptJobs, type OrderRow } from "../../model/adapters";
import { numberAttempts } from "../../model/job-map-rows";
import { useOrderJobsColumns } from "../../model/use-order-jobs-columns";

type Props = {
  /**
   * The order's jobs on the map, newest first (see `jobMapRows`). The map lists one job
   * per order, so the order's other attempts — on any day — are fetched by its number.
   */
  jobs: OrderRow[];
  /** Saved column order and widths, shared by every order's table. */
  columnConfig?: ColumnConfigState;
  onColumnConfigChange?: (config: ColumnConfigState) => void;
  /** Opens the job in the edit dialog. */
  onJobIdClick?: (row: OrderRow) => void;
  onRouteClick?: (routeId: bigint) => void;
};

// Inside an order's card: no frame of its own, a 24px header with a rule below it (the
// order's row draws the one above), and rows without dividers.
//
// The map's own table reaches in through descendant selectors: it makes this table
// border-separate, where a row's border is never drawn, so the rule sits on the header
// cells; and it styles header cells as a grey cell behind a white rounded bar, undone here.
const NESTED_TABLE_CLASS_NAME = [
  "!rounded-none !border-0 !bg-transparent",
  "[&_th]:!h-6 [&_th]:!bg-transparent [&_th]:text-[11px] [&_th]:border-b [&_th]:border-slate-200",
  "[&_th>div:first-child]:!rounded-none [&_th>div:first-child]:!border-0 [&_th>div:first-child]:!bg-transparent",
  // 4px beside each header label and cell, so the table fits inside the order's card at 1440px
  "[&_th_span]:!px-1 [&_td]:!px-1 [&_td]:!py-2",
].join(" ");

const NO_INCIDENTS: readonly ConnectProto.DriverIncident[] = [];

const jobRowKey = (row: OrderRow) => String(row.id);
const jobRowClassName = () => "border-0";

export const OrderJobsTable = ({
  jobs,
  columnConfig,
  onColumnConfigChange,
  onJobIdClick,
  onRouteClick,
}: Props) => {
  const t = useT();
  const { isReceiverUser } = useAuthSession();

  // A job without an order number is an order of its own: nothing more to fetch. Jobs
  // sharing a number are one order only while they also share provider, customer, site
  // and job type (some imported numbers span several), so all of them narrow the list.
  const { orderNumber, serviceProviderId, serviceReceiverId, pickupLocationId, type } =
    jobs[0].rawJob;
  const { attempts } = useListJobsQuery(
    {
      orderNumbers: [orderNumber],
      serviceProviderIds: [serviceProviderId],
      serviceReceiverIds: [serviceReceiverId],
      pickupLocationIds: [pickupLocationId],
      jobTypes: [type],
    },
    { skip: !orderNumber, selectFromResult: ({ data }) => ({ attempts: data?.jobs }) },
  );
  const rows = useMemo(
    () => (attempts ? numberAttempts(adaptJobs(attempts, t)) : jobs),
    [attempts, jobs, t],
  );

  // The listed jobs' incidents, one request per opened order. By job, not by route: a
  // job expired at midnight never had one. Incidents are the provider's to see: a receiver
  // user reads a Невывоз from the job alone (debt, or its shift's force-completion).
  const jobIds = useMemo(() => rows.map((row) => BigInt(row.rawJob.id)), [rows]);
  const { incidents } = useListDriverIncidentsQuery(
    { ownerOrganizationId: BigInt(serviceProviderId), jobIds },
    {
      skip: isReceiverUser || (!!orderNumber && !attempts) || jobIds.length === 0,
      selectFromResult: ({ data }) => ({ incidents: data?.driverIncidents }),
    },
  );
  const columns = useOrderJobsColumns({
    onJobIdClick,
    onRouteClick,
    incidents: isReceiverUser ? NO_INCIDENTS : incidents,
  });

  return (
    <DataTable
      data={orderNumber && !attempts ? [] : rows}
      loading={!!orderNumber && !attempts}
      loadingRows={1}
      columns={columns}
      columnConfig={columnConfig}
      onColumnConfigChange={onColumnConfigChange}
      namespace={Namespace.Orders}
      rowKey={jobRowKey}
      rowClassName={jobRowClassName}
      stickyHeader={false}
      zebra={false}
      className={NESTED_TABLE_CLASS_NAME}
    />
  );
};
